import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  RefreshCw,
  X,
  Plus,
  ShoppingCart,
  MoreVertical,
  Eye,
  Pencil,
  Check,
  Receipt,
  XCircle,
  Calendar,
  BarChart3,
  Link2,
  ExternalLink,
  Download,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FlaskConical,
  Upload,
} from 'lucide-react';
import purchaseService from '../services/purchaseService';
import excelExportService from '../services/excelExportService';
import Layout from '../components/Layout';
import PurchaseApprovalBadge from '../components/PurchaseApprovalBadge';
import TemplateSelector from '../components/TemplateSelector';
import ContractImportDialog from '../components/ContractImportDialog';
import useEnhancedPagination from '../hooks/useEnhancedPagination';
import useFilterPersistence from '../hooks/useFilterPersistence';
import authService from '../services/authService';

const PurchasesPage = () => {
  const navigate = useNavigate();
  const user = authService.getCurrentUser();
  const canViewPrices = user && ['admin', 'accounting'].includes(user.role);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calculate default date range (last 30 days)
  const getDefaultDateRange = () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };

  // Use filter persistence hook with default values
  const [persistedState, setPersistedState] = useFilterPersistence('purchase-orders', {
    filters: {
      purchase_order_id: '',
      supplier_name: '',
      status: '',
      approval_status: '',
      fulfillment_status: '',
      source_sales_order_id: ''
    },
    dateRange: getDefaultDateRange(),
    page: 1,
    page_size: 10,
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Extract filters and dateRange from persisted state
  const filters = persistedState.filters;
  const dateRange = persistedState.dateRange;

  // Update filter values
  const setFilters = (newFilters) => {
    setPersistedState(prev => ({
      ...prev,
      filters: typeof newFilters === 'function' ? newFilters(prev.filters) : newFilters
    }));
  };

  // Update date range values
  const setDateRange = (newDateRange) => {
    setPersistedState(prev => ({
      ...prev,
      dateRange: typeof newDateRange === 'function' ? newDateRange(prev.dateRange) : newDateRange
    }));
  };

  // Enhanced pagination hook with persisted initial values
  const {
    pagination,
    sorting,
    handlePageChange: originalHandlePageChange,
    handleSortChange: originalHandleSortChange,
    updatePaginationData,
    resetPagination,
    paginationInfo
  } = useEnhancedPagination({
    initialPage: persistedState.page,
    initialPageSize: persistedState.page_size,
    defaultSortBy: persistedState.sortBy,
    defaultSortOrder: persistedState.sortOrder,
    enableVirtualization: true,
    virtualizationThreshold: 50
  });

  // Wrap pagination handlers to persist changes
  const handlePageChange = useCallback((newPage) => {
    originalHandlePageChange(null, newPage);
    setPersistedState(prev => ({ ...prev, page: newPage }));
  }, [originalHandlePageChange, setPersistedState]);

  const handleSortChange = useCallback((field) => {
    const isAsc = sorting.orderBy === field && sorting.order === 'asc';
    const newSorting = { orderBy: field, order: isAsc ? 'desc' : 'asc' };
    originalHandleSortChange(newSorting);
    setPersistedState(prev => ({
      ...prev,
      sortBy: newSorting.orderBy,
      sortOrder: newSorting.order
    }));
  }, [sorting, originalHandleSortChange, setPersistedState]);

  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', order: null, reason: '' });
  const [showContractImportDialog, setShowContractImportDialog] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [linkStats, setLinkStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [selectedOrderForExport, setSelectedOrderForExport] = useState(null);

  const loadPurchaseOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await purchaseService.getPurchaseOrders(
        pagination.page,
        pagination.page_size,
        {
          ...filters,
          start_date: dateRange.startDate,
          end_date: dateRange.endDate,
          sort_by: sorting.orderBy,
          sort_order: sorting.order
        }
      );

      if (response.purchase_orders && Array.isArray(response.purchase_orders)) {
        setPurchaseOrders(response.purchase_orders);
      } else if (Array.isArray(response)) {
        setPurchaseOrders(response);
      } else {
        console.error('Unexpected response structure:', response);
        setPurchaseOrders([]);
      }

      if (response.pagination) {
        updatePaginationData(response);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.page_size, filters, sorting, dateRange, updatePaginationData]);

  const loadLinkStats = useCallback(async () => {
    try {
      setLoadingStats(true);
      const stats = await purchaseService.getPurchaseOrderLinkStatus();
      setLinkStats(stats);
    } catch (err) {
      console.error('Error loading link stats:', err);
    } finally {
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    loadPurchaseOrders();
    loadLinkStats();
  }, [loadPurchaseOrders, loadLinkStats]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value === 'all' ? '' : value
    }));
  };

  const handleFilterSubmit = (e) => {
    if (e) e.preventDefault();
    resetPagination();
  };

  const handleClearFilter = (filterName) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: ''
    }));
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleClearAllFilters = () => {
    setFilters({
      purchase_order_id: '',
      supplier_name: '',
      status: '',
      approval_status: '',
      fulfillment_status: '',
      source_sales_order_id: ''
    });
    setDateRange(getDefaultDateRange());
  };

  const handleAction = (action, order) => {
    if (action === 'view') {
      navigate(`/purchases/${order._id}/view`, { state: { orderData: order } });
    } else if (action === 'edit') {
      navigate(`/purchases/${order._id}/edit`);
    } else if (action === 'view_so') {
      const salesOrderId = order.source_sales_order?.sales_order_object_id || order.source_sales_order?._id || order.sales_order_id;
      if (salesOrderId) {
        navigate(`/sales/${salesOrderId}/view`);
      }
    } else if (action === 'export') {
      handleExportToExcel(order);
    } else if (['approve', 'reject', 'cancel', 'receipt'].includes(action)) {
      setConfirmDialog({ open: true, action, order, reason: '' });
    } else if (action === 'convert') {
      navigate('/purchases/convert-from-sales');
    }
  };

  const handleExportToExcel = (order) => {
    setSelectedOrderForExport(order);
    setTemplateSelectorOpen(true);
  };

  const handleTemplateSelected = async (template) => {
    try {
      if (!selectedOrderForExport) return;

      const { blob, filename } = await excelExportService.exportUsingTemplate('purchase', {
        template_id: template.template_id,
        purchase_order_ids: [selectedOrderForExport._id]
      });

      excelExportService.downloadBlob(blob, filename);
      console.log('Export completed:', filename);
      setTimeout(() => alert('采购单导出成功！'), 100);
    } catch (error) {
      console.error('Export failed:', error);
      setError('导出失败: ' + (error.message || 'Unknown error'));
    }
    setTemplateSelectorOpen(false);
    setSelectedOrderForExport(null);
  };

  const handleConfirmAction = async () => {
    const { action, order, reason } = confirmDialog;
    setConfirmDialog({ open: false, action: '', order: null, reason: '' });

    try {
      if (action === 'approve') {
        await purchaseService.approvePurchaseOrder(order._id);
        setPurchaseOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, approval_status: 'approved', status: 'confirmed' } : o
        ));
        setError('');
        setTimeout(() => alert('Purchase order approved successfully!'), 100);
      } else if (action === 'reject') {
        await purchaseService.rejectPurchaseOrder(order._id, reason);
        setPurchaseOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, approval_status: 'rejected', status: 'cancelled' } : o
        ));
        setError('');
        setTimeout(() => alert('Purchase order rejected successfully!'), 100);
      } else if (action === 'cancel') {
        await purchaseService.cancelPurchaseOrder(order._id);
        setPurchaseOrders(prev => prev.map(o =>
          o._id === order._id ? { ...o, status: 'cancelled' } : o
        ));
        setError('');
        setTimeout(() => alert('Purchase order cancelled successfully!'), 100);
      } else if (action === 'receipt') {
        navigate(`/purchases/${order._id}/receipt`);
      }
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} purchase order`);
    }
  };

  const getStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'draft': return 'outline';
      case 'submitted': return 'secondary';
      case 'confirmed': return 'default';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  const getFulfillmentStatusVariant = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'outline';
      case 'partial': return 'secondary';
      case 'complete': return 'default';
      default: return 'outline';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  const getStatusLabel = (status, type = 'status') => {
    if (type === 'approval') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待审批';
        case 'approved': return '已批准';
        case 'rejected': return '已拒绝';
        default: return status || '待审批';
      }
    } else if (type === 'fulfillment') {
      switch (status?.toLowerCase()) {
        case 'pending': return '待收货';
        case 'partial': return '部分收货';
        case 'complete': return '已完成';
        default: return status || '待收货';
      }
    } else {
      switch (status?.toLowerCase()) {
        case 'draft': return '草稿';
        case 'submitted': return '已提交';
        case 'confirmed': return '已确认';
        case 'cancelled': return '已取消';
        default: return status || '草稿';
      }
    }
  };

  const headCells = useMemo(() => {
    const cells = [
      { id: 'purchase_order_id', label: '采购单编号' },
      { id: 'supplier_name', label: '供应商' },
      { id: 'source_sales_order', label: '关联销售单', sortable: false },
      { id: 'status', label: '状态' },
      { id: 'approval_status', label: '审批状态', sortable: false },
      { id: 'fulfillment_status', label: '收货状态', sortable: false },
      { id: 'total_products', label: '产品数量' },
    ];
    if (canViewPrices) {
      cells.push({ id: 'total_cost', label: '总金额' });
    }
    cells.push(
      { id: 'created_at', label: '创建日期' },
      { id: 'actions', label: '操作', sortable: false }
    );
    return cells;
  }, [canViewPrices]);

  const SortIcon = ({ columnId }) => {
    if (sorting.orderBy !== columnId) return <ArrowUpDown className="ml-1 h-4 w-4 text-muted-foreground" />;
    return sorting.order === 'asc'
      ? <ArrowUp className="ml-1 h-4 w-4" />
      : <ArrowDown className="ml-1 h-4 w-4" />;
  };

  const getDialogTitle = () => {
    switch (confirmDialog.action) {
      case 'approve': return '批准采购单';
      case 'reject': return '拒绝采购单';
      case 'cancel': return '取消采购单';
      case 'receipt': return '添加收货记录';
      default: return '';
    }
  };

  const getDialogDescription = () => {
    switch (confirmDialog.action) {
      case 'approve':
        return `确定要批准采购单 ${confirmDialog.order?.purchase_order_id} 吗？`;
      case 'reject':
        return `确定要拒绝采购单 ${confirmDialog.order?.purchase_order_id} 吗？`;
      case 'cancel':
        return `确定要取消采购单 ${confirmDialog.order?.purchase_order_id} 吗？此操作不可撤销。`;
      case 'receipt':
        return `将为采购单 ${confirmDialog.order?.purchase_order_id} 添加收货记录。`;
      default:
        return '';
    }
  };

  return (
    <Layout title="采购管理" activeTab="purchases">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              {error}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setError('')}>
                <X className="h-4 w-4" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Header with Action Buttons */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-foreground">采购订单</h1>
          <div className="flex gap-2">
            {canViewPrices && (
              <Button
                variant="outline"
                onClick={() => setShowMetrics(!showMetrics)}
              >
                <BarChart3 className="mr-2 h-4 w-4" />
                成本分析
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => navigate('/purchases/convert-from-sales')}
            >
              <ShoppingCart className="mr-2 h-4 w-4" />
              从销售单转换
            </Button>
            <Button onClick={() => navigate('/purchases/new')}>
              <Plus className="mr-2 h-4 w-4" />
              新建采购单
            </Button>
            <Button variant="outline" onClick={() => setShowContractImportDialog(true)}>
              <Upload className="mr-2 h-4 w-4" />
              导入合同
            </Button>
          </div>
        </div>

        {/* SO-PO Link Statistics */}
        {linkStats && (
          <Card className="mb-6 bg-primary/10 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-primary flex items-center">
                  <Link2 className="mr-2 h-5 w-5" />
                  销售单-采购单关联统计
                </h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadLinkStats}
                  disabled={loadingStats}
                >
                  {loadingStats ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  刷新
                </Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">{linkStats.linked_purchase_orders || 0}</p>
                  <p className="text-sm text-muted-foreground">关联采购单</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-success">{linkStats.unique_sales_orders || 0}</p>
                  <p className="text-sm text-muted-foreground">关联销售单</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-primary">{linkStats.total_mappings || 0}</p>
                  <p className="text-sm text-muted-foreground">总映射数</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-warning">
                    {((linkStats.linked_purchase_orders || 0) / Math.max(linkStats.total_purchase_orders || 1, 1) * 100).toFixed(1)}%
                  </p>
                  <p className="text-sm text-muted-foreground">关联比例</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metrics Panel */}
        {canViewPrices && showMetrics && (
          <Card className="mb-6 bg-muted/30">
            <CardContent className="pt-6">
              <MetricsPanel />
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="purchase_order_id"
                    placeholder="按采购单编号筛选..."
                    value={filters.purchase_order_id}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.purchase_order_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('purchase_order_id')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="supplier_name"
                    placeholder="按供应商名称筛选..."
                    value={filters.supplier_name}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.supplier_name && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('supplier_name')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-w-[180px]">
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    name="source_sales_order_id"
                    placeholder="按关联销售单筛选..."
                    value={filters.source_sales_order_id}
                    onChange={handleFilterChange}
                    className="pl-10 pr-8"
                  />
                  {filters.source_sales_order_id && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                      onClick={() => handleClearFilter('source_sales_order_id')}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Status Filters */}
              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">状态</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => handleSelectFilterChange('status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="draft">草稿</SelectItem>
                    <SelectItem value="submitted">已提交</SelectItem>
                    <SelectItem value="confirmed">已确认</SelectItem>
                    <SelectItem value="cancelled">已取消</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">审批状态</Label>
                <Select value={filters.approval_status || 'all'} onValueChange={(v) => handleSelectFilterChange('approval_status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="pending">待审批</SelectItem>
                    <SelectItem value="approved">已批准</SelectItem>
                    <SelectItem value="rejected">已拒绝</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="min-w-[120px]">
                <Label className="text-xs mb-1 block">收货状态</Label>
                <Select value={filters.fulfillment_status || 'all'} onValueChange={(v) => handleSelectFilterChange('fulfillment_status', v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">全部</SelectItem>
                    <SelectItem value="pending">待收货</SelectItem>
                    <SelectItem value="partial">部分收货</SelectItem>
                    <SelectItem value="complete">已完成</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="h-8 w-px bg-border mx-1" />

              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div className="space-y-1">
                  <Label className="text-xs">开始日期</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => handleDateRangeChange('startDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
                <span className="text-sm text-muted-foreground">至</span>
                <div className="space-y-1">
                  <Label className="text-xs">结束日期</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => handleDateRangeChange('endDate', e.target.value)}
                    className="h-9 w-[140px]"
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="ghost"
                size="icon"
                disabled={loading}
                title="刷新"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={handleClearAllFilters}
                disabled={loading}
                title="清除所有筛选"
              >
                <X className="h-4 w-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Purchase Orders Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        {headCells.map((headCell) => (
                          <th
                            key={headCell.id}
                            className="px-4 py-3 text-left font-semibold text-foreground"
                          >
                            {headCell.sortable !== false ? (
                              <button
                                className="flex items-center hover:text-primary transition-colors"
                                onClick={() => handleSortChange(headCell.id)}
                              >
                                {headCell.label}
                                <SortIcon columnId={headCell.id} />
                              </button>
                            ) : (
                              headCell.label
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {purchaseOrders.map((order) => (
                        <tr key={order._id} className="hover:bg-muted/30">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <button
                                className="text-primary underline hover:text-primary/80 transition-colors font-medium"
                                onClick={() => navigate(`/purchases/${order._id}/view`)}
                              >
                                {order.purchase_order_id}
                              </button>
                              {order.is_sample && (
                                <FlaskConical className="h-4 w-4 text-purple-500" title="样品单" />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">{order.supplier_name}</td>
                          <td className="px-4 py-3">
                            {order.source_sales_order ? (
                              <button
                                className="text-primary underline hover:text-primary/80 transition-colors flex items-center gap-1"
                                onClick={() => {
                                  const salesOrderId = order.source_sales_order?.sales_order_object_id || order.source_sales_order?._id || order.sales_order_id;
                                  if (salesOrderId) {
                                    navigate(`/sales/${salesOrderId}/view`);
                                  }
                                }}
                              >
                                {order.source_sales_order.sales_order_id}
                                <ExternalLink className="h-3 w-3" />
                              </button>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={getStatusVariant(order.status)}>
                              {getStatusLabel(order.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            <PurchaseApprovalBadge
                              status={order.approval_status}
                              size="small"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={getFulfillmentStatusVariant(order.fulfillment_status)}>
                              {getStatusLabel(order.fulfillment_status, 'fulfillment')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">{order.total_products || 0} 项</td>
                          {canViewPrices && (
                            <td className="px-4 py-3 font-bold">{formatCurrency(order.total_cost)}</td>
                          )}
                          <td className="px-4 py-3">{formatDate(order.created_at)}</td>
                          <td className="px-4 py-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => handleAction('view', order)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  查看详情
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleAction('edit', order)}>
                                  <Pencil className="mr-2 h-4 w-4" />
                                  编辑采购单
                                </DropdownMenuItem>
                                {order.source_sales_order && (
                                  <DropdownMenuItem onClick={() => handleAction('view_so', order)}>
                                    <Link2 className="mr-2 h-4 w-4" />
                                    查看关联销售单
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem onClick={() => handleAction('export', order)}>
                                  <Download className="mr-2 h-4 w-4" />
                                  导出Excel
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {order.approval_status === 'pending' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleAction('approve', order)}>
                                      <Check className="mr-2 h-4 w-4" />
                                      批准
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleAction('reject', order)}>
                                      <X className="mr-2 h-4 w-4" />
                                      拒绝
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {order.status === 'confirmed' && (
                                  <DropdownMenuItem onClick={() => handleAction('receipt', order)}>
                                    <Receipt className="mr-2 h-4 w-4" />
                                    添加收货记录
                                  </DropdownMenuItem>
                                )}
                                {order.status !== 'cancelled' && order.fulfillment_status === 'pending' && (
                                  <DropdownMenuItem
                                    onClick={() => handleAction('cancel', order)}
                                    className="text-destructive focus:text-destructive"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    取消采购单
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      ))}

                      {purchaseOrders.length === 0 && (
                        <tr>
                          <td colSpan={headCells.length} className="px-4 py-8 text-center text-muted-foreground">
                            未找到采购单
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination.total_pages > 1 && (
                  <div className="flex justify-center py-4 border-t">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page - 1)}
                        disabled={pagination.page <= 1}
                      >
                        上一页
                      </Button>
                      <span className="text-sm text-muted-foreground px-4">
                        第 {pagination.page} / {pagination.total_pages} 页
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePageChange(pagination.page + 1)}
                        disabled={pagination.page >= pagination.total_pages}
                      >
                        下一页
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <p className="mt-4 text-sm text-muted-foreground">
          正在显示 {paginationInfo.startItem}-{paginationInfo.endItem} / {paginationInfo.totalItems} 条采购单
        </p>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: '', order: null, reason: '' })}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
              <DialogDescription>{getDialogDescription()}</DialogDescription>
            </DialogHeader>
            {confirmDialog.action === 'reject' && (
              <div className="py-4">
                <Label htmlFor="reject-reason">拒绝原因</Label>
                <Textarea
                  id="reject-reason"
                  placeholder="请输入拒绝原因..."
                  value={confirmDialog.reason}
                  onChange={(e) => setConfirmDialog(prev => ({ ...prev, reason: e.target.value }))}
                  className="mt-2"
                  rows={3}
                />
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmDialog({ open: false, action: '', order: null, reason: '' })}
              >
                取消
              </Button>
              <Button
                variant={confirmDialog.action === 'cancel' || confirmDialog.action === 'reject' ? 'destructive' : 'default'}
                onClick={handleConfirmAction}
              >
                {confirmDialog.action === 'approve' && '批准'}
                {confirmDialog.action === 'reject' && '拒绝'}
                {confirmDialog.action === 'cancel' && '取消'}
                {confirmDialog.action === 'receipt' && '确定'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template Selector Dialog */}
        <TemplateSelector
          open={templateSelectorOpen}
          onClose={() => {
            setTemplateSelectorOpen(false);
            setSelectedOrderForExport(null);
          }}
          onSelect={handleTemplateSelected}
          entityType="purchase"
          language="cn"
          title="选择采购订单导出模板"
        />

        <ContractImportDialog
          open={showContractImportDialog}
          onClose={() => setShowContractImportDialog(false)}
        />
      </div>
    </Layout>
  );
};

// Metrics Panel Component
const MetricsPanel = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const data = await purchaseService.getCostAnalytics();
        setAnalytics(data);
      } catch (error) {
        console.error('Error loading analytics:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-muted-foreground">无法加载成本分析数据</p>;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY'
    }).format(amount || 0);
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card>
        <CardContent className="pt-6">
          <p className="text-lg font-semibold text-primary">总采购成本</p>
          <p className="text-2xl font-bold">{formatCurrency(analytics.summary?.confirmed_purchase_cost || 0)}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <p className="text-lg font-semibold text-success">利润率</p>
          <p className="text-2xl font-bold">{analytics.summary?.profit_margin_percent || 0}%</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <p className="text-lg font-semibold text-primary">采购单数量</p>
          <p className="text-2xl font-bold">{analytics.summary?.confirmed_purchase_orders || 0}</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <p className="text-lg font-semibold text-warning">预估利润</p>
          <p className="text-2xl font-bold">{formatCurrency(analytics.summary?.estimated_profit || 0)}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PurchasesPage;
